import {
  useCardContext,
  useExportCroppedSecondaryImage,
  useExportLiveView,
} from '@hts/entities/Card/libs';
import { ISmsConsentData } from '@hts/entities/Card/types';
import {
  getLogoForRequest,
  getMappedOrderTextFields,
  getMappedSaveDraftTextFields,
  getSecondaryImageForRequest,
  setLogoFormDataForRequest,
  setSecondaryImageFormDataForRequest,
} from '@hts/entities/Card/utils';
import { useSaveOrderDraftMutation, useSetOrderMutation } from '@hts/shared/api';
import { STRIPE_PUBLIC_KEY } from '@hts/shared/consts';
import { useAppSelector } from '@hts/shared/libs';
import sizes from '@hts/shared/styles/Sizes.module.scss';
import { ICreateOrderDataOutput, ISaveOrderDraftRequest } from '@hts/shared/types';
import { HtsButton } from '@hts/shared/ui';
import { useMediaQuery } from '@mui/material';
import * as Sentry from '@sentry/react';
import { loadStripe, Stripe, TokenResult } from '@stripe/stripe-js';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConfirmPopup } from '../ConfirmPopup';
import { ThanksPopup } from '../ThanksPopup';
import BuyingPopup from './BuyingPopup';
import DraftPopup from './DraftPopup';

interface IBuyButtonsProps {
  price: number;
}

enum EPopup {
  CONFIRM = 'confirm',
  BUYING = 'buying',
  DRAFT = 'draft',
}

type IPaymentData = TokenResult | { testPurchase: boolean };

export const BuyButtons: React.FC<IBuyButtonsProps> = memo(({ price }) => {
  const confirmPopup = useRef(null);
  const buyPopup = useRef(null);
  const draftPopup = useRef(null);

  const card = useCardContext((state) => state.card);

  const currentUser = useAppSelector((state) => state.mainReducer.currentUser);

  const [setOrderRequest] = useSetOrderMutation();
  const [saveOrderDraftRequest] = useSaveOrderDraftMutation();

  const isMobile = useMediaQuery(`(max-width: ${sizes.mobileWidth})`);

  const [activePopup, setActivePopup] = useState<EPopup | null>(null);
  const [showThanks, setShowThanks] = useState(false);
  const [stripePromise, setStripePromise] = useState<Stripe | null>(null);
  const [email, setEmail] = useState('');
  const [draftEmail, setDraftEmail] = useState('');
  const [draftEmailStatus, setDraftEmailStatus] = useState(false);
  const [draftSuccess, setDraftSuccess] = useState(true);
  const [draftLoading, setDraftLoading] = useState(false);
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);
  const [isWhatsAppNotificationEnabled, setIsWhatsAppNotificationEnabled] = useState(false);
  const [whatsappPhoneNumber, setWhatsappPhoneNumber] = useState(null);
  const [stripeError, setStripeError] = useState(false);

  const { renderExportLiveView, getStageBlob: getStageBlobForLiveView } = useExportLiveView({
    card,
  });

  const { renderExportCroppedSecondaryImage, getStageBlob: getStageBlobForCroppedSecondaryImage } =
    useExportCroppedSecondaryImage({
      card,
    });

  const cardName = card?.name || 'card';

  const adminBuyEmails = useMemo(
    () => [
      'info@finaltouchny.com',
      'danylo.podopryhora25@gmail.com',
      'orders@ihopetoseeyou.com',
      'maksym.baranovskyi@itechcraft.com',
      'vladyslav.onatskyi@itechcraft.com',
    ],
    []
  );

  useEffect(() => {
    const initLoadStripe = async () => {
      const newStripePromise = await loadStripe(STRIPE_PUBLIC_KEY);

      setStripePromise(newStripePromise);
    };

    initLoadStripe();
  }, []);

  useEffect(() => {
    if (typeof currentUser === 'object' && currentUser && 'email' in currentUser) {
      setEmail(currentUser.email as string);
    }
  }, [currentUser]);

  const setPaid = useCallback(() => {
    sessionStorage.clear();

    setActivePopup(null);
    setShowThanks(true);
  }, []);

  const openPopup = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, popup: EPopup) => {
      e?.preventDefault();

      setActivePopup(popup);
    },
    []
  );

  const closePopup = useCallback((e) => {
    if (e) e.preventDefault();

    setActivePopup(null);
    setStripeError(false);
  }, []);

  const onCloseThanks = useCallback(() => {
    setShowThanks(false);
  }, []);

  const saveDraft = useCallback(
    async (e) => {
      if (e) e.preventDefault();

      if (!card) return;

      setDraftLoading(true);

      if (draftEmail.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)) {
        const newOrder = {
          email: draftEmail,
          card_id: card.cardId,
          fields: [...card.fields],
          audio_id: card.audio_id ? '' + card.audio_id : undefined,
        };

        newOrder.fields.forEach((item) => {
          item.font_id = item.font?.id;
        });

        try {
          const variables: ISaveOrderDraftRequest = {
            email: draftEmail,
            cardId: Number(card.cardId),
            audioId: Number(card.audio_id) || undefined,
            textFields: getMappedSaveDraftTextFields(card.fields),
            secondaryImageData: null,
            logoData: null,
          };

          // if (card.secondaryImage) {
          //   variables.secondaryImageData = getSecondaryImageForRequest(card.secondaryImage);
          // }

          // if (card.logo) {
          //   variables.logoData = getLogoForRequest(card.logo);
          // }

          const result = await saveOrderDraftRequest(variables);

          if ('data' in result) {
            setDraftEmailStatus(true);
          } else {
            setDraftEmailStatus(false);
          }

          setDraftLoading(false);
        } catch (err) {
          Sentry.captureException(err, {
            tags: { source: 'saveDraft' },
            extra: { cardId: card?.cardId },
          });

          console.log('Error', err);

          setDraftSuccess(false);
          setDraftLoading(false);
        }
      }
    },
    [card, draftEmail, saveOrderDraftRequest]
  );

  const inputChangeHandler = useCallback((e) => {
    const { name, value } = e.target;

    if (name === 'draft_email') {
      setDraftEmail(value);
    } else if (name === 'email') {
      setEmail(value);
    }
  }, []);

  const handleSetOrder = useCallback(
    async (paymentData: IPaymentData | null, smsConsentData?: ISmsConsentData) => {
      if (!card) {
        Sentry.captureMessage('SetOrder: card is not defined', {
          level: 'error',
          tags: { screen: 'order', phase: 'init' },
          extra: { email },
        });

        console.error('SetOrder: card is not defined');

        return;
      }

      setLoadingStripe(true);
      setDisabledButton(true);

      const mainCardImageBlob = await getStageBlobForLiveView();

      if (!mainCardImageBlob) {
        Sentry.captureMessage('SetOrder: mainCardImageBlob is not defined', {
          level: 'warning',
          tags: { screen: 'order', phase: 'generate-image' },
          extra: { cardId: card?.cardId, email },
        });

        console.error('SetOrder: mainCardImageBlob is not defined');

        setLoadingStripe(false);
        setDisabledButton(false);
        setStripeError(true);

        return;
      }

      const formData = new FormData();

      const data: ICreateOrderDataOutput = {
        cardId: Number(card.cardId),
        email,
        audioId: Number(card.audio_id) || undefined,
        stripeDetails: JSON.stringify(paymentData),
        textFields: getMappedOrderTextFields(card.fields),
      };

      const secondaryImageState = card.secondaryImage;
      const logoState = card.logo;

      if (smsConsentData?.smsConsent) {
        data.mobile = smsConsentData.phoneNumber;
        data.doesAgreeReceiveMMS = smsConsentData.smsConsent;
      }

      if (secondaryImageState) {
        data.secondaryImageData = getSecondaryImageForRequest(secondaryImageState);

        const isCorrect = await setSecondaryImageFormDataForRequest({
          secondaryImage: secondaryImageState,
          formData,
          getStageBlobForCroppedSecondaryImage,
        });

        if (!isCorrect) {
          Sentry.captureMessage('SetOrder: secondary image blob failed', {
            level: 'warning',
            tags: { screen: 'order', phase: 'secondary-image' },
            extra: { cardId: card?.cardId, hasSecondary: !!card.secondaryImage },
          });

          setLoadingStripe(false);
          setDisabledButton(false);
          setStripeError(true);

          return;
        }
      }

      if (logoState) {
        data.logoData = getLogoForRequest(logoState);

        setLogoFormDataForRequest({ logo: logoState, formData });
      }

      formData.append('orderedCardFile', mainCardImageBlob);
      formData.append('data', JSON.stringify(data));

      try {
        const result = await setOrderRequest(formData);

        setLoadingStripe(false);

        if ('data' in result) {
          setPaid();
          setEmail(email);

          sessionStorage.clear();

          setDisabledButton(false);
          setStripeError(false);
        } else {
          setDisabledButton(false);
          setStripeError(true);
        }
      } catch (err) {
        Sentry.captureException(err, {
          tags: { screen: 'order', action: 'submit-form' },
          extra: {
            email,
            cardId: card?.cardId,
            hasSecondary: !!card?.secondaryImage,
            hasLogo: !!card?.logo,
          },
        });

        console.log('Error', err);

        setLoadingStripe(false);
        setDisabledButton(false);
      }
    },
    [
      setOrderRequest,
      email,
      card,
      getStageBlobForLiveView,
      setPaid,
      getStageBlobForCroppedSecondaryImage,
    ]
  );

  const whatsAppNotificationHandler = useCallback(() => {
    setIsWhatsAppNotificationEnabled((prev) => !prev);
  }, []);

  const whatsAppPhoneNumberHandler = useCallback((value) => {
    setWhatsappPhoneNumber(value);
  }, []);

  const onClickDraft = useCallback(() => {
    setActivePopup(EPopup.DRAFT);
  }, []);

  const onClickBuy = useCallback(() => {
    setActivePopup(EPopup.CONFIRM);
  }, []);

  const onClickTestBuy = useCallback(() => {
    handleSetOrder({ testPurchase: true });
  }, [handleSetOrder]);

  const popup = useMemo(() => {
    switch (activePopup) {
      case EPopup.CONFIRM:
        return (
          <ConfirmPopup
            ref={confirmPopup}
            nextPopup={(e) => openPopup(e, EPopup.BUYING)}
            closePopup={closePopup}
          />
        );
      case EPopup.BUYING:
        return (
          <BuyingPopup
            ref={buyPopup}
            stripePromise={stripePromise}
            email={email}
            inputChangeHandler={inputChangeHandler}
            currentUser={currentUser}
            setOrder={handleSetOrder}
            loadingStripe={loadingStripe}
            setLoadingStripe={setLoadingStripe}
            price={price}
            cardName={cardName}
            isWhatsAppNotificationEnabled={isWhatsAppNotificationEnabled}
            whatsAppNotificationHandler={whatsAppNotificationHandler}
            whatsAppPhoneNumberHandler={whatsAppPhoneNumberHandler}
            whatsAppPhone={whatsappPhoneNumber}
            closePopup={closePopup}
            stripeError={stripeError}
          />
        );
      case EPopup.DRAFT:
        return (
          <DraftPopup
            ref={draftPopup}
            draft_email={draftEmail}
            inputChangeHandler={inputChangeHandler}
            draftEmailStatus={draftEmailStatus}
            saveDraft={saveDraft}
            draftSuccess={draftSuccess}
            loading={draftLoading}
            closePopup={closePopup}
          />
        );
      default:
        return null;
    }
  }, [
    activePopup,
    cardName,
    closePopup,
    currentUser,
    draftEmail,
    draftEmailStatus,
    draftLoading,
    draftSuccess,
    email,
    inputChangeHandler,
    isWhatsAppNotificationEnabled,
    loadingStripe,
    openPopup,
    price,
    saveDraft,
    handleSetOrder,
    stripeError,
    stripePromise,
    whatsAppNotificationHandler,
    whatsAppPhoneNumberHandler,
    whatsappPhoneNumber,
  ]);

  return (
    <>
      {renderExportLiveView}
      {renderExportCroppedSecondaryImage}

      {popup}

      {showThanks && <ThanksPopup email={email} closePopup={onCloseThanks} />}

      <HtsButton
        label="Save as draft"
        title="Save as draft"
        variant="outlined"
        fullWidth={isMobile}
        typographyProps={{ fontWeight: '600' }}
        style={{ maxWidth: isMobile ? 320 : 'auto' }}
        disabled={disabledButton}
        onClick={onClickDraft}
      />

      <HtsButton
        label="Buy Card"
        title="Buy Card"
        fullWidth={isMobile}
        typographyProps={{ fontWeight: '600' }}
        style={{ maxWidth: isMobile ? 320 : 'auto' }}
        disabled={disabledButton}
        onClick={onClickBuy}
      />

      {Boolean(adminBuyEmails.includes(email)) && (
        <HtsButton
          label="Buy Test Card"
          title="Buy Test Card"
          fullWidth={isMobile}
          typographyProps={{ fontWeight: '600' }}
          style={{ maxWidth: isMobile ? 320 : 'auto' }}
          disabled={disabledButton}
          onClick={onClickTestBuy}
        />
      )}
    </>
  );
});
